import {
  ComponentType,
  KeyboardEvent as ReactKeyboardEvent,
  ReactNode,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Search, UserCheck, Users, Users2 } from 'lucide-react';
import type { PaletteDestination } from '../utils/adminTabs';
import { enrollmentHref, invoiceHref, parentHref, studentHref } from '../utils/recordLinks';
import {
  ADMIN_SEARCH_MIN_CHARS,
  type RecordHit,
  type RecordKind,
  toKeywords,
  useAdminSearch,
} from '../utils/useAdminSearch';

type Icon = ComponentType<{ className?: string }>;

export type { PaletteDestination };

interface PaletteItem {
  kind: 'page' | 'tab' | RecordKind;
  /** The destination key (`finances`, `finances:payments`), or the record's id. */
  id: string;
  label: string;
  detail: string;
  badge: string | null;
  icon: Icon;
  href: string;
}

interface PaletteSection {
  key: string;
  label: string;
  items: PaletteItem[];
}

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
  destinations: PaletteDestination[];
}

export function Kbd({ children }: { children: ReactNode }) {
  return (
    <kbd className="inline-flex items-center justify-center min-w-[1.25rem] px-1 py-0.5 rounded border border-gray-200 bg-white text-[11px] font-sans font-medium leading-none text-gray-500">
      {children}
    </kbd>
  );
}

/**
 * ⌘K search across the admin app: jump to a page or one of its tabs, or to a student,
 * parent, enrollment or invoice.
 *
 * Mounted only while open, so every opening starts from an empty box at the top of the list.
 */
export function CommandPalette({ open, ...props }: CommandPaletteProps) {
  if (!open) return null;
  return <PaletteDialog {...props} />;
}

const RECORD_ICON: Record<RecordKind, Icon> = {
  student: Users,
  parent: Users2,
  enrollment: UserCheck,
  invoice: FileText,
};

const RECORD_HREF: Record<RecordKind, (id: string) => string> = {
  student: studentHref,
  parent: parentHref,
  enrollment: enrollmentHref,
  invoice: invoiceHref,
};

function toRecordItem(hit: RecordHit): PaletteItem {
  return {
    kind: hit.kind,
    id: hit.id,
    label: hit.name,
    detail: hit.detail,
    badge: hit.badge,
    icon: RECORD_ICON[hit.kind],
    href: RECORD_HREF[hit.kind](hit.id),
  };
}

function PaletteDialog({ onClose, destinations }: Omit<CommandPaletteProps, 'open'>) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const listId = useId();

  const records = useAdminSearch(query);
  const trimmed = query.trim();

  const sections = useMemo<PaletteSection[]>(() => {
    const keywords = toKeywords(trimmed);
    // The empty box lists the sidebar's pages, in its order. Tabs join once there is
    // something to match: thirty-odd of them would bury the pages they belong to.
    const pages = destinations
      .filter(d => (keywords.length === 0
        ? !d.isSubTab
        : keywords.every(k => `${d.label} ${d.detail} ${d.keywords ?? ''}`.toLowerCase().includes(k))))
      .map(d => ({
        kind: d.isSubTab ? 'tab' as const : 'page' as const,
        id: d.key,
        label: d.label,
        detail: d.detail,
        badge: null,
        icon: d.icon,
        href: d.href,
      }));
    return [
      { key: 'pages', label: 'Pages', items: pages },
      { key: 'students', label: 'Students', items: records.students.map(toRecordItem) },
      { key: 'parents', label: 'Parents', items: records.parents.map(toRecordItem) },
      { key: 'enrollments', label: 'Enrollments', items: records.enrollments.map(toRecordItem) },
      { key: 'invoices', label: 'Invoices', items: records.invoices.map(toRecordItem) },
    ].filter(section => section.items.length > 0);
  }, [trimmed, destinations, records.students, records.parents, records.enrollments, records.invoices]);

  const items = useMemo(() => sections.flatMap(section => section.items), [sections]);
  // Results arrive after the index was set, and can arrive shorter; never point past the end.
  const current = items.length === 0 ? -1 : Math.min(activeIndex, items.length - 1);
  const optionId = (index: number) => `${listId}-option-${index}`;

  // Focus goes to the box on open and back to wherever it was on close, so ⌘K from a
  // half-typed search field returns the cursor to that field.
  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    inputRef.current?.focus();
    return () => previouslyFocused?.focus?.();
  }, []);

  useEffect(() => {
    if (current < 0) return;
    const el = listRef.current?.querySelector<HTMLElement>(`[data-index="${current}"]`);
    // jsdom has no layout, so no scrollIntoView.
    el?.scrollIntoView?.({ block: 'nearest' });
  }, [current]);

  const select = (item: PaletteItem) => {
    onClose();
    navigate(item.href);
  };

  const onKeyDown = (e: ReactKeyboardEvent<HTMLInputElement>) => {
    // Pinyin and other IMEs pick characters with Enter and the arrows. While a composition is
    // open those keys belong to the IME, not the list — otherwise typing 王 would jump to
    // whatever the latin letters typed so far happened to match. Safari reports the Enter
    // that commits a composition with isComposing already false but keyCode 229.
    if (e.nativeEvent.isComposing || e.keyCode === 229) return;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (items.length > 0) setActiveIndex((current + 1) % items.length);
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (items.length > 0) setActiveIndex((current - 1 + items.length) % items.length);
        break;
      case 'Enter':
        e.preventDefault();
        if (current >= 0) select(items[current]);
        break;
      case 'Escape':
        e.preventDefault();
        e.stopPropagation();
        onClose();
        break;
      case 'Tab':
        // The box is the only stop; the list is driven from it.
        e.preventDefault();
        break;
    }
  };

  const recordCount = records.students.length + records.parents.length
    + records.enrollments.length + records.invoices.length;
  const tooShortForRecords = trimmed.length > 0 && trimmed.length < ADMIN_SEARCH_MIN_CHARS;
  const status = records.isError
    ? 'Could not search people, enrollments and invoices.'
    : records.isSearching && recordCount === 0
      ? 'Searching people, enrollments and invoices…'
      : tooShortForRecords
        ? `Type ${ADMIN_SEARCH_MIN_CHARS}+ letters to search people, enrollments and invoices.`
        : items.length === 0 && trimmed
          ? `Nothing matches “${trimmed}”.`
          : null;

  let offset = 0;

  return (
    <div className="fixed inset-0 z-[70] flex items-start justify-center px-4 pt-[10vh] sm:pt-[12vh]">
      <div className="absolute inset-0 bg-gray-900/40" onClick={onClose} aria-hidden="true" />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Search the admin portal"
        data-command-palette
        className="relative w-full max-w-xl bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden"
      >
        <div className="flex items-center gap-3 px-4 border-b border-gray-200">
          <Search className="h-4 w-4 text-gray-400 flex-shrink-0" aria-hidden="true" />
          <input
            ref={inputRef}
            type="text"
            role="combobox"
            aria-expanded={items.length > 0}
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={current >= 0 ? optionId(current) : undefined}
            aria-label="Search pages, people, enrollments and invoices"
            placeholder="Search pages, people, enrollments, invoices…"
            autoComplete="off"
            spellCheck={false}
            value={query}
            onChange={e => {
              setQuery(e.target.value);
              setActiveIndex(0);
            }}
            onKeyDown={onKeyDown}
            className="flex-1 min-w-0 h-12 text-sm text-gray-900 placeholder:text-gray-400 bg-transparent outline-none"
          />
          <span className="hidden sm:inline-flex"><Kbd>esc</Kbd></span>
        </div>

        <div ref={listRef} className="max-h-[50vh] sm:max-h-[60vh] overflow-y-auto py-1">
          <div id={listId} role="listbox" aria-label="Results">
            {sections.map(section => {
              const start = offset;
              offset += section.items.length;
              const headingId = `${listId}-${section.key}`;
              return (
                <div key={section.key} role="group" aria-labelledby={headingId} className="py-1">
                  <div id={headingId} className="px-4 pt-1.5 pb-1 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                    {section.label}
                  </div>
                  {section.items.map((item, i) => {
                    const index = start + i;
                    const isActive = index === current;
                    const ItemIcon = item.icon;
                    return (
                      <div
                        key={`${item.kind}-${item.id}`}
                        id={optionId(index)}
                        role="option"
                        aria-selected={isActive}
                        data-index={index}
                        // mousemove, not mouseenter: a list scrolling under a resting pointer
                        // should not take the highlight away from the arrow keys.
                        onMouseMove={() => { if (!isActive) setActiveIndex(index); }}
                        // Keep focus in the box, or the arrows stop working after a click.
                        onMouseDown={e => e.preventDefault()}
                        onClick={() => select(item)}
                        className={`flex items-center gap-3 mx-2 px-2 py-2 rounded-lg cursor-pointer text-sm ${
                          isActive ? 'bg-gray-100 text-gray-900' : 'text-gray-700'
                        }`}
                      >
                        <ItemIcon className="h-4 w-4 flex-shrink-0 text-gray-400" />
                        <span className="truncate font-medium">{item.label}</span>
                        {item.badge && (
                          <span className="flex-shrink-0 px-1.5 py-0.5 rounded bg-gray-100 text-[10px] font-medium text-gray-500">
                            {item.badge}
                          </span>
                        )}
                        {item.detail && (
                          <span className="ml-auto pl-2 truncate text-xs text-gray-400">{item.detail}</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
          {status && (
            <p role="status" className="px-4 py-3 text-sm text-gray-500">{status}</p>
          )}
        </div>

        <div className="hidden sm:flex items-center gap-4 px-4 py-2 border-t border-gray-100 text-[11px] text-gray-400">
          <span className="flex items-center gap-1"><Kbd>↑</Kbd><Kbd>↓</Kbd> move</span>
          <span className="flex items-center gap-1"><Kbd>↵</Kbd> open</span>
          <span className="flex items-center gap-1"><Kbd>esc</Kbd> close</span>
        </div>
      </div>
    </div>
  );
}
